import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, setFilters } from '../redux/slices/clientUsersSlice';
import { useSearchParams } from 'react-router-dom';
import { ROWS_PER_PAGE, DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '../utils/paginationUtils';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TablePagination from '@mui/material/TablePagination';
import ClientUsersDrawer from '../components/ClientUsersDrawer';
import NoData from '../components/NoData';
import MenuItem from '@mui/material/MenuItem';

function ClientUsers() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [openCreate, setOpenCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const { data: usersData, error: usersDataError, totalItems, options } = useSelector((state) => state.clientUsers);

  useEffect(() => {
    const filtersFromUrl = {
      page: parseInt(searchParams.get('page')) || DEFAULT_PAGE,
      limit: parseInt(searchParams.get('limit')) || DEFAULT_ROWS_PER_PAGE,
      search: searchParams.get('search') || '',
    };

    dispatch(setFilters(filtersFromUrl));
    dispatch(fetchUsers(filtersFromUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchParams.get('page'), searchParams.get('limit')]);

  useEffect(() => {
    setSearchParams(
      {
        page: options.page,
        limit: options.limit,
        search: options.search || '',
        status: options.status || 'ALL',
      },
      { replace: true }
    );
  }, [options.page, options.limit, options.search, options.status, searchParams, setSearchParams]);

  const updateFilters = (patch) => {
    const updated = { ...options, ...patch };
    dispatch(setFilters(updated));
    dispatch(fetchUsers(updated));
    setSearchParams(updated);
  };

  const handleOnSearchClick = () => updateFilters({ page: 1 });
  const handleChangePage = (_, newPage) => updateFilters({ page: newPage + 1 });
  const handleChangeRowsPerPage = (e) => updateFilters({ limit: +e.target.value, page: 1 });

  const onDrawerClose = () => {
    setOpenCreate(false);
    setEditUser(null);
  };

  const onCreateOpen = () => {
    setOpenCreate(true);
  };

  const onUpdateOpen = (user) => {
    setEditUser(user);
  };

  useEffect(() => {
    if (usersDataError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [usersDataError, t]);

  return (
    <div className="users">
      <Grid sx={{ mb: 4 }} container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h3">{t('Client Users')}</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={onCreateOpen}>
            {t('Add user')}
          </Button>
        </Grid>
      </Grid>
      <Paper sx={{ p: 1, mb: 2 }}>
        <Grid container alignItems="center" spacing={2} justifyContent="flex-end">
          <Grid item xs={12} sm={8} md={4}>
            <TextField
              onChange={(event) => dispatch(setFilters({ search: event.target.value }))}
              value={options.search}
              sx={{ marginRight: 4 }}
              inputProps={{ autoComplete: 'off' }}
              label={t('Search')}
              fullWidth
              placeholder={t('Search by email, phone, last name or first name')}
              variant="outlined"
              type="text"
              size="small"
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleOnSearchClick} edge="end">
                    <SearchOutlined />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={0} md={2}>
            <TextField
              onChange={(event) => {
                dispatch(setFilters({ status: event.target.value }));
                updateFilters({ status: event.target.value });
              }}
              value={options.status}
              sx={{ marginRight: 3 }}
              label={t('Status')}
              fullWidth
              select
              size="small"
              variant="outlined"
              type="text"
            >
              <MenuItem key={0} value={'ALL'}>
                {t('All')}
              </MenuItem>
              <MenuItem key={1} value={'ACTIVE'}>
                {t('ACTIVE')}
              </MenuItem>
              <MenuItem key={1} value={'INACTIVE'}>
                {t('INACTIVE')}
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {usersData.length ? (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">{t('Id')}</TableCell>
                  <TableCell align="left">{t('Created')}</TableCell>
                  <TableCell align="left">{t('Email')}</TableCell>
                  <TableCell align="left">{t('Phone')}</TableCell>
                  <TableCell align="left">{t('First Name')}</TableCell>
                  <TableCell align="left">{t('Last Name')}</TableCell>
                  <TableCell align="left">{t('Role')}</TableCell>
                  <TableCell align="left">{t('Status')}</TableCell>
                  <TableCell align="right">{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersData.map(({ id, created_at, email, phone, first_name, last_name, role, status }, index) => (
                  <TableRow
                    key={id}
                    sx={{
                      textAlign: 'right',
                      '&:last-child td, &:last-child th': { borderBottom: 0 },
                      '&:nth-of-type(odd)': (theme) => ({
                        backgroundColor: theme.palette.action.hover,
                      }),
                    }}
                  >
                    <TableCell>{id}</TableCell>
                    <TableCell>{dayjs(created_at).format('DD/MM/YYYY HH:mm')}</TableCell>
                    <TableCell>{email}</TableCell>
                    <TableCell>{phone}</TableCell>
                    <TableCell>{first_name}</TableCell>
                    <TableCell>{last_name}</TableCell>
                    <TableCell>{role}</TableCell>
                    <TableCell>
                      {status === 'ACTIVE' ? (
                        <Chip
                          sx={{ mt: 1, borderRadius: 1 }}
                          size="small"
                          label={`${t(status)}`}
                          variant="outlined"
                          color="success"
                        />
                      ) : (
                        <Chip
                          sx={{ mt: 1, borderRadius: 1 }}
                          size="small"
                          label={`${status}`}
                          variant="outlined"
                          color="error"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" aria-label="update" onClick={() => onUpdateOpen(usersData[index])}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={options.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={options.limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE}
            labelRowsPerPage={t('rowsPaginator')}
          />
        </Paper>
      ) : (
        <NoData />
      )}
      <ClientUsersDrawer open={openCreate || !!editUser} user={editUser} onClose={onDrawerClose} />
    </div>
  );
}

export default ClientUsers;
